import classNames from 'classnames';
import React from 'react';
import { UseQueryResult } from 'react-query';
import { IssueCandidate, PollResponse, Result } from 'api/api.types';
import { useTranslation } from 'react-i18next';
import ElectionTeaserInfo from '../../lib/PollTeaser/ElectionTeaserInfo';
import CandidateBarGroup from './CandidateBarGroup';

interface Props {
  query: UseQueryResult<PollResponse, unknown>;
  isFinal: boolean;
}

export const appendRemainingVotesEntry = (
  candidateList: IssueCandidate[],
  results: Result,
  string: string,
  round: number,
  showAsElected = false,
): IssueCandidate[] => {
  if (results && 'single_valid' in results.majority_election_data && round !== 2) {
    candidateList.push({
      id: null,
      first_name: string,
      last_name: '',
      results: {
        votes: results.majority_election_data?.single_valid || 0,
        votes_from_unchanged: 0,
        votes_from_changed: 0,
        elected: showAsElected,
      },
    });
  }
  return candidateList;
};

const CandidateBarsCanton = ({ query, isFinal }: Props) => {
  const { t } = useTranslation('wap');
  if (query.status === 'loading') return null;

  const electionDayOverview = query.data.data;
  const absoluteMajority =
    query.data.data.results.majority_election_data.absolute_majority;
  const seatsToBeElected = query.data.data.info.majority_election_info.seat_count;

  const sortedCandidates = [...electionDayOverview.candidates].sort(
    (a, b) => b.results.votes - a.results.votes,
  );
  const maxVotes = sortedCandidates[0].results.votes;

  const hasNoWinner = absoluteMajority > maxVotes;

  const showMajorityLine = Boolean(absoluteMajority);

  const majorityLineOffset = hasNoWinner ? 100 : (absoluteMajority / maxVotes) * 100;
  const majorityLineStyle = {
    '--offset-majority-line-left': `${majorityLineOffset}%`,
  } as React.CSSProperties;

  const isMajorityLineFarRight = majorityLineOffset > 90;

  const nextElectionDate =
    query.data?.data?.results?.majority_election_data?.second_round_date;
  const roundNumber = query.data?.data?.info?.majority_election_info?.round_number;

  const getCandidateList = (returnElected: boolean) => {
    if (isFinal) {
      if (returnElected) {
        return sortedCandidates.filter(
          (candidate) => candidate.results.elected === returnElected,
        );
      }
      return appendRemainingVotesEntry(
        sortedCandidates.filter(
          (candidate) => candidate.results.elected === returnElected,
        ),
        query.data?.data?.results,
        t('single_votes'),
        roundNumber,
      );
    }

    const numberOfCurrentlyElectedCandidates = sortedCandidates
      .filter((candidate) => candidate.results.votes > (absoluteMajority || 0))
      .slice(0, seatsToBeElected).length;

    if (returnElected) {
      return sortedCandidates.slice(0, numberOfCurrentlyElectedCandidates);
    }

    const notElectedCandidates = sortedCandidates.slice(
      numberOfCurrentlyElectedCandidates,
    );

    return appendRemainingVotesEntry(
      notElectedCandidates,
      query.data?.data?.results,
      t('single_votes'),
      roundNumber,
    );
  };

  const electedCandidates = getCandidateList(true);
  const notElectedCandidates = getCandidateList(false);

  const notAllSeatsFilled =
    isFinal &&
    electedCandidates.length > 0 &&
    electedCandidates.length < seatsToBeElected;
  const noSeatsFilled = isFinal && electedCandidates.length === 0;

  return (
    <div className="electiondetails__wrapper">
      <div className="electiondetails__outer">
        {noSeatsFilled && (
          <ElectionTeaserInfo
            noSeats
            nextElectionDate={nextElectionDate}
            round={roundNumber}
          />
        )}
        <div
          className={classNames('electiondetails__inner', {
            'electiondetails__inner--with-majority': showMajorityLine,
          })}
          style={majorityLineStyle}
        >
          {showMajorityLine && (
            <>
              <div className="electiondetails__majority-line" />
              <div
                className={classNames('electiondetails__majority-info', {
                  'electiondetails__majority-info--switch-side': isMajorityLineFarRight,
                })}
              >
                <span>
                  {`${t('formatted_number', { count: absoluteMajority })} `}
                  <span className="poll__visuallyhidden">
                    {`${t('absolute_majority_screen_reader_info')} `}
                  </span>
                </span>
                <span>
                  {isFinal ? t('absolute_majority_final') : t('absolute_majority_prelim')}
                </span>
              </div>
            </>
          )}
          {electedCandidates.length > 0 && (
            <CandidateBarGroup
              issueId={query.data.data.info.id}
              list={electedCandidates}
              isElected
              isFinal={isFinal}
              hasNoWinner={hasNoWinner}
              absoluteMajority={absoluteMajority}
              maxVotes={maxVotes}
            />
          )}
          {notAllSeatsFilled && (
            <ElectionTeaserInfo
              noSeats={false}
              nextElectionDate={nextElectionDate}
              round={roundNumber}
            />
          )}
          {notElectedCandidates.length > 0 && (
            <CandidateBarGroup
              issueId={query.data.data.info.id}
              list={notElectedCandidates}
              isElected={false}
              isFinal={isFinal}
              hasNoWinner={hasNoWinner}
              absoluteMajority={absoluteMajority}
              maxVotes={maxVotes}
            />
          )}
        </div>
      </div>
      <div aria-hidden className="electiondetails__placeholder candidatebar__votes">
        {maxVotes}
      </div>
    </div>
  );
};

export default CandidateBarsCanton;
